import React, { useState, useEffect } from 'react';
import { Activity, ShieldAlert, Building2, UserPlus, CheckCircle2 } from 'lucide-react';
import { timeAgo } from '../../utils/formatDate';
import { notificationApi } from '../../api/notificationApi';

const ActivityFeed = ({ socket }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    try {
      const res = await notificationApi.getNotifications({ limit: 15 });
      const list = res.data.notifications || [];
      const mapped = list.map(item => {
        let Icon = CheckCircle2;
        let color = 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
        
        if (item.type === 'limit_request') {
          Icon = ShieldAlert;
          color = 'text-amber-600 bg-amber-50 border-amber-100';
        } else if (item.type === 'company_onboarded' || item.type === 'system') {
          Icon = Building2;
          color = 'text-green-700 bg-green-50 border-green-100';
        } else {
          color = 'text-emerald-600 bg-emerald-50 border-emerald-100';
        }

        return {
          id: item._id,
          type: item.type,
          icon: Icon,
          color: color,
          title: item.title || 'System Alert',
          message: item.message,
          timestamp: item.createdAt
        };
      });
      setActivities(mapped);
    } catch (err) {
      console.error('Failed to fetch notifications for feed:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();

    if (socket) {
      const handleNewNotification = () => {
        fetchNotifications();
      };

      socket.on('notification_received', handleNewNotification);
      socket.on('chat_reply_received', handleNewNotification);
      return () => {
        socket.off('notification_received', handleNewNotification);
        socket.off('chat_reply_received', handleNewNotification);
      };
    }
  }, [socket]);

  return (
    <div className="p-5 rounded-2xl bg-white border border-slate-100 shadow-sm flex flex-col h-full">
      <div className="flex items-center space-x-3 mb-5">
        <div className="p-2 bg-green-50 rounded-xl">
          <Activity className="w-4 h-4 text-green-700" />
        </div>
        <h3 className="text-sm font-bold text-slate-900 font-display">Live System Stream</h3>
      </div>

      <div className="space-y-3 overflow-y-auto flex-1 pr-1">
        {loading ? (
          <div className="py-8 text-center text-sm text-slate-400 font-medium">Loading system stream...</div>
        ) : activities.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-400 font-medium">No recent system notifications.</div>
        ) : (
          activities.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.id}
                className="flex items-start space-x-4 p-4 rounded-2xl bg-slate-50/50 border border-slate-100 transition hover:bg-slate-50 hover:shadow-sm"
              >
                <div className={`p-2.5 rounded-xl border flex-shrink-0 ${item.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-bold text-slate-900 truncate">{item.title}</h4>
                    <span className="text-[11px] font-medium text-slate-400">{timeAgo(item.timestamp)}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1.5 leading-relaxed line-clamp-2">{item.message}</p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ActivityFeed;
